import { LoraSelect } from "@components/lora-select";
import { ResponsiveSelect } from "@components/responsive-select";
import { useCameraModes } from "@features/camera/modes";
import { useLoras } from "@features/loras/use-loras";
import { useEffectPlease } from "@features/utils";
import { ArrowLeftIcon, Squares2X2Icon } from "@heroicons/react/16/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import {
  Callout,
  Heading,
  IconButton,
  ScrollArea,
  Tooltip,
} from "@radix-ui/themes";
import { getPreviousPath } from "@views/layout/layout";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { useCameraForm } from "./camera-form-context";
import { CameraFormFooter } from "./components/form-footer";
import { ClassicMode } from "./modes/classic-mode";
import { HaircutMode } from "./modes/haircut-mode";
import { ImageMode } from "./modes/image-mode";
import { PackMode } from "./modes/pack-mode";
import { TryonMode } from "./modes/tryon-mode";

export const CameraForm = ({
  onChange,
  onRun,
}: {
  onChange?: (arg: {
    model: string;
    style: string;
    pack: string;
    project: string;
  }) => void;
  onRun?: () => void;
} = {}) => {
  const { t } = useTranslation();
  const { modes: cameraModes } = useCameraModes();
  const { site } = useParams();
  const { loras } = useLoras();

  const {
    mode,
    setMode,
    model,
    setModel,
    style,
    setStyle,
    pack,
    setPack,
    promptTxt,
    setPromptTxt,
    image: fromImage,
    setFromImage,
    similarity,
    setSimilarity,
    refetchFromUrl,
    handleFormSubmit,
  } = useCameraForm();

  // Check if user has no models at all
  const userModels = (loras.data || []).filter(
    (a) => a.type === "model" && a.status !== "failed"
  );
  const hasNoModels = loras.data && userModels.length === 0 && model === "none";

  useEffectPlease(() => {
    refetchFromUrl();
  }, []);

  // Handle automatic form generation from URL state
  const location = useLocation();
  useEffectPlease(() => {
    const generatePhotosNow = !!location.state?.run;
    window.history.replaceState({}, "");
    if (generatePhotosNow) {
      setTimeout(() => {
        handleFormSubmit();
      }, 100);
    }
  }, []);

  return (
    <div className="space-y-2 flex flex-col h-full pb-4 overflow-hidden -mx-4">
      <div className="pt-4 space-x-2 hidden md:flex items-center md:px-4 pl-2 relative">
        <Link to={getPreviousPath()} className="hidden md:block">
          <IconButton size="1" variant="soft" color="gray">
            <ArrowLeftIcon className="h-4 w-4" />
          </IconButton>
        </Link>
        <ResponsiveSelect
          className="bg-transparent border-none outline-none ring-0 px-0"
          value={mode}
          onChange={(a) => setMode(a as any)}
          items={Object.entries(cameraModes).map(
            ([key, { label, icon: Icon }]) => ({
              value: key,
              label: (
                <div className="flex items-center">
                  <Icon className="h-4 w-4 mr-2" />
                  <span>{label}</span>
                </div>
              ),
            })
          )}
          renderTrigger={() => (
            <Heading size="2" className="inline-block">
              {cameraModes[mode as keyof typeof cameraModes]?.label ||
                t("camera.form.none")}
            </Heading>
          )}
        />

        <div className="">
          <Tooltip content={t("camera.form.open_templates_packs")}>
            <Link to={`/${site}/templates`}>
              <IconButton
                size="2"
                variant="soft"
                className="!rounded-full absolute right-4 top-4"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </IconButton>
            </Link>
          </Tooltip>
        </div>
      </div>

      <ScrollArea scrollbars="vertical" className="grow md:pt-4 px-4">
        <div className="space-y-4">
          {hasNoModels && (
            <Callout.Root color="amber" size="1">
              <Callout.Icon>
                <ExclamationTriangleIcon className="h-4 w-4" />
              </Callout.Icon>
              <Callout.Text>
                {t(
                  "camera.form.no_model_warning",
                  "You don't have any model, create a model first or use one of the public models."
                )}{" "}
                <Link
                  to={`/${site || "portraits"}/lora/model`}
                  className="underline font-medium hover:text-amber-800"
                >
                  {t("camera.form.create_model", "Create a model")}
                </Link>
              </Callout.Text>
            </Callout.Root>
          )}

          <div className="space-y-2">
            <Heading size="2">{t("camera.form.model")}</Heading>
            <LoraSelect
              value={model}
              onChange={setModel}
              type="model"
              canEdit
            />
          </div>

          {mode === "pack" && <PackMode pack={pack} setPack={setPack} />}

          {mode === "classic" && (
            <ClassicMode
              promptTxt={promptTxt}
              setPromptTxt={setPromptTxt}
              fromImage={fromImage}
              setFromImage={setFromImage}
              style={style}
              setStyle={setStyle}
            />
          )}

          {mode === "tryon" && (
            <TryonMode
              fromImage={fromImage}
              setFromImage={setFromImage}
              promptTxt={promptTxt}
              setPromptTxt={setPromptTxt}
            />
          )}

          {mode === "haircut" && (
            <HaircutMode
              fromImage={fromImage}
              setFromImage={setFromImage}
              promptTxt={promptTxt}
              setPromptTxt={setPromptTxt}
            />
          )}

          {mode === "image" && (
            <ImageMode
              fromImage={fromImage}
              setFromImage={setFromImage}
              similarity={similarity}
              setSimilarity={setSimilarity}
            />
          )}
        </div>
      </ScrollArea>

      <CameraFormFooter />
    </div>
  );
};
